import type {
  WaterTreeHeader,
  WaterTreeHierarchyNode,
} from '@/services/dashboardService'

/**
 * Header text for the water-tree details page.
 *
 * Both values used to be wrong: the tree name came only from a `?treeName=`
 * query param (so it fell back to the raw id whenever the linking page didn't
 * supply it, e.g. the water-tree map or the notifications button, or on
 * refresh), and the line under it was the hardcoded literal
 * "כ.מים אב: 200139 | מס׳ מד אב: 6788514", identical for every tree. Both now
 * come from GET /api/water-tree/{accountId}/{zoneId}/header.
 */

/**
 * Pick the best available name for the tree, in order of trustworthiness:
 * the fetched header, then the URL hint, then the bare id as a last resort.
 * The param is kept only so the title doesn't flicker on first paint.
 */
export function resolveTreeName(
  header: WaterTreeHeader | null | undefined,
  treeNameParam: string | null,
  zoneId: string | undefined
): string {
  const fromHeader = header?.zoneName?.trim()
  if (fromHeader) return fromHeader

  const fromParam = treeNameParam?.trim()
  if (fromParam) return fromParam

  return zoneId ?? ''
}

/** Shown in place of a value the database has no answer for. */
const MISSING = '—'

/**
 * The small line under the title: the supplying premise's water card and its
 * current meter number, separated by ' | '. Always these two labels, so the
 * header keeps a stable shape.
 *
 * Both describe the tree's master premise — one level up where the hierarchy has
 * a parent, the tree's own premise where it is a root. Groups take theirs from a
 * group_premises row with role = 'supplier'; no group has one attached today, so
 * those render as '—'. Nothing is substituted from other fields and no
 * placeholder numbers are invented — the previous
 * "כ.מים אב: 200139 | מס׳ מד אב: 6788514" matched no premise in the database.
 */
export function formatTreeSubtitle(
  header: WaterTreeHeader | null | undefined
): string {
  if (!header) return ''

  const waterCard = header.parentBillingId ?? MISSING
  const meterNumber = header.parentMeterNumber ?? MISSING

  return `כ.מים אב: ${waterCard} | מס׳ מד אב: ${meterNumber}`
}

/**
 * The supply chain shown under the tabs, replacing the placeholder tail
 * "> מספק הבא > מד שכונתי > מד העיר".
 *
 * Deliberately does NOT repeat "מספק: {tree}" — ConsumerHeader already renders
 * exactly that as the page title, so including it here printed the tree name
 * twice on the page. This shows only what the title doesn't: where the tree gets
 * its water from.
 *
 * The nodes come from GET /api/water-tree/{accountId}/{zoneId}/hierarchy-map,
 * which resolves a single level: the immediate parent (premises.parent_id) plus
 * alternative parents and children. A tree with no parent (a site main, or a
 * group with nothing attached) has nothing to add, so this returns '' and the
 * line is not rendered at all.
 */
export function buildBreadcrumb(
  nodes: WaterTreeHierarchyNode[] | null | undefined
): string {
  const parent = nodes
    ?.find(node => node.relationship === 'parent')
    ?.nodeName?.trim()

  return parent ? `מקבל מים מ: ${parent}` : ''
}
